import React, { useState } from 'react';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fmtN } from './JoBits';
import { JoStageSheet } from './JoStageSheet';
import { JoTimeline } from './JoTimeline';
import { JoSources } from './JoSources';

const TOTALS = [['sent_pcs', 'Sent', 'text-gray-900'], ['done_pcs', 'Done in SFG', 'text-emerald-700'], ['in_progress_pcs', 'In progress', 'text-sky-700'],
  ['lost_pcs', 'Lost', 'text-rose-700'], ['off_route_pcs', 'Off route', 'text-amber-700'], ['not_sent_pcs', 'Not sent yet', 'text-gray-500']];

export const tone = (s) => {
  if (s.lost_pcs > 0) return 'border-rose-300 bg-rose-50';
  if (s.stuck) return 'border-amber-400 bg-amber-50';
  if (s.waiting_pcs > 0) return 'border-sky-300 bg-white';
  if (s.done_pcs > 0) return 'border-emerald-300 bg-emerald-50';
  return 'border-[#E5DFD0] bg-gray-50 text-gray-400';
};

const Totals = ({ t }) => (
  <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
    {TOTALS.map(([k, label, cls]) => (
      <div key={k} className="rounded-lg border border-[#E5DFD0] bg-white px-3 py-2">
        <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
        <div className={`text-2xl font-bold tabular-nums ${cls}`} data-testid={`jo-total-${k}`}>{fmtN(t[k])}</div>
      </div>
    ))}
  </div>
);

export const passedWord = (key) => (key === 'damage' ? 'checked' : 'done');

const Tile = ({ s, last, onOpen }) => (
  <div className="flex flex-1 min-w-[112px] items-center">
    <button type="button" onClick={() => onOpen(s)} data-testid={`jo-stage-${s.key}`}
      className={`w-full min-w-0 rounded-lg border-2 px-2.5 py-2 text-left transition-transform hover:-translate-y-0.5 hover:shadow-md ${tone(s)}`}>
      <div className="text-xs font-semibold truncate" title={s.label}>{s.label}</div>
      <div className="mt-1 text-lg font-bold tabular-nums text-emerald-700" data-testid={`jo-stage-${s.key}-done`}>{fmtN(s.done_pcs)}<span className="ml-1 text-[10px] font-normal text-gray-500">{passedWord(s.key)}</span></div>
      <div className="text-[11px] tabular-nums truncate" data-testid={`jo-stage-${s.key}-waiting`}>{s.waiting_pcs > 0 ? <span className={s.stuck ? 'font-semibold text-amber-800' : 'text-sky-800'}>{fmtN(s.waiting_pcs)} waiting · {s.oldest_days}d</span> : <span className="text-gray-400">none waiting</span>}</div>
      {s.lost_pcs > 0 && <div className="text-[11px] font-semibold text-rose-700" data-testid={`jo-stage-${s.key}-lost`}>{fmtN(s.lost_pcs)} rejected</div>}
    </button>
    {!last && <ChevronRight className="w-3 h-3 mx-px text-gray-300 shrink-0" />}
  </div>
);

const OffRoute = ({ rows, route }) => (!rows.length ? null : (
  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="jo-offroute">
    <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4" />Off route — this is a {route === 'FLEXI' ? 'Flexi' : 'Inhouse'} route order, but these pieces went the other way</p>
    <ul className="mt-1 space-y-0.5">{rows.map((r, i) => <li key={`${r.stage}-${r.code}`} data-testid={`jo-offroute-row-${i}`}><span className="font-mono">{r.code}</span> · {r.label} · {fmtN(r.pcs)} pcs</li>)}</ul>
  </div>
));

/** Where every piece of the job order is now: totals, a stage-by-stage strip (tap for bundles / boxes), timeline and sources. */
export const JoJourney = ({ j }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-4" data-testid="jo-journey">
      <Totals t={j.totals} />
      <Card className="border-0 shadow-md"><CardHeader className="pb-2"><CardTitle className="text-base">Journey to SFG Storage</CardTitle>
        <p className="text-xs text-gray-500">Green: passed · Amber: waiting more than 3 days · Red: rejected at the Damage Section · Tap a stage for its bundles and boxes</p></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex overflow-x-auto pb-2" data-testid="jo-stage-strip">{j.stages.map((s, i) => <Tile key={s.key} s={s} last={i === j.stages.length - 1} onOpen={setOpen} />)}</div>
          <OffRoute rows={j.off_route || []} route={j.route} />
        </CardContent></Card>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="border-0 shadow-md lg:col-span-2"><CardHeader className="pb-2"><CardTitle className="text-base">Timeline</CardTitle></CardHeader>
          <CardContent><JoTimeline stages={j.stages} /></CardContent></Card>
        <Card className="border-0 shadow-md lg:col-span-3"><CardHeader className="pb-2"><CardTitle className="text-base">Bundles sent · source</CardTitle>
          <p className="text-xs text-gray-500">Greige document, supplier and Purchase Order of every bundle picked for this order</p></CardHeader>
          <CardContent className="p-0"><JoSources rows={j.bundles || []} /></CardContent></Card>
      </div>
      <JoStageSheet stage={open} onClose={() => setOpen(null)} />
    </div>
  );
};
